<template>
  <div class="view-contents">
    <div class="row title-row">
      <div class="col section-title section-header">New Projects Budget Status</div>
    </div>
    <div v-if='dashboardLoading'>
      <LoadingSpinner size='2x'/>
    </div>
    <div v-else>
      <span>Select Fiscal Year</span>
      <b-form-select v-model="selectedYear" :options='fiscalYears' class="m-2" size="sm"/>
      <span>Select Program</span>
      <b-form-select
        v-model="selectedProgram"
        :options='programs'
        label-field='name'
        class="m-2" size="sm">
        <template #first>
          <b-form-select-option :value="null" disabled>
            -- Select a Program to View --
          </b-form-select-option>
        </template>
      </b-form-select>
      <div v-if="selectedProgram">
        <div class="section-title">{{ selectedProgram}}</div>
        <div class="row bold-title sticky-top">
          <div class="col-4"></div>
          <div class="col-2 text-right">
            Total Budget
          </div>
          <div class="col-2 text-right">
            Encumbered Funds
          </div>
          <div class="col-2 text-right">
            Remaining Funds
          </div>
          <div class="col-2 text-right">
            Requested Funds
          </div>
        </div>
        <div class="card"
          v-for="fundingSource in fundingSourcesForTable(selectedProgram)"
          :key="fundingSource.name">
          <div class="card-header dashboard-detail-card">
            <div class="row">
              <div class="col-4">
                <a v-b-toggle="`funding-source-${fundingSource.id}`" class="c-pointer">
                  <b-icon icon='caret-right-fill' class="when-closed" />
                  <b-icon icon='caret-down-fill' class="when-opened" />
                  {{ fundingSource.name }}
                </a>
              </div>
              <div class="col-2 text-right">
                {{ fundingSource.totalBudget | toCurrency }}
              </div>
              <div class="col-2 text-right">
                {{ fundingSource.encumberedFunds | toCurrency }}
              </div>
              <div class="col-2 text-right">
                {{ fundingSource.remainingFunds | toCurrency }}
              </div>
              <div class="col-2 text-right">
                {{ fundingSource.requestedFunds | toCurrency }}
              </div>
            </div>
          </div>
          <b-collapse :id="`funding-source-${fundingSource.id}`">
            <div class="card-body">
              <div
                class="row"
                v-for="projectFund in fundingSource.projectFundings"
                :key="projectFund.projectId">
                <div class="col-3 offset-1">
                  <router-link
                    :to="{ name: 'ProjectBudget', params: { id: projectFund.projectId }}"
                  >
                    {{ projectFund.projectName }}
                    ({{ projectFund.rankingScore ? projectFund.rankingScore : 'No Ranking' }})
                  </router-link>
                </div>
                <div class="col-2">
                </div>
                <div class="col-2 text-right">
                  {{ projectFund.encumberedCostShareAmount | toCurrency }}
                </div>
                <div class="col-2">
                </div>
                <div class="col-2 text-right">
                  {{ projectFund.requestedCostShareAmount | toCurrency }}
                </div>
              </div>
            </div>
          </b-collapse>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import FiscalYear from '@/mixins/fiscalYear';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

export default {
  name: 'NewProjectsBudgetStatus',
  components: {
    LoadingSpinner,
  },
  mixins: [
    FiscalYear,
  ],
  computed: {
    ...mapState(['config']),
    ...mapState('Project', {
      serverData: (state) => state.dashboardData,
      dashboardLoading: (state) => state.dashboardLoading,
    }),
    fiscalYears() {
      return this.serverData.map((data) => data.year).sort((a, b) => a - b);
    },
    programs() {
      const selectedData = this.serverData
        .find((data) => data.year === this.selectedYear);
      if (selectedData) {
        return [...new Set(selectedData.fundingSources.filter((data) => data.programName !== '')
          .map((data) => data.programName))]
          .sort((a, b) => a.localeCompare(b));
      }
      return [];
    },
  },
  async mounted() {
    await this.$store.dispatch('Project/getProjectBudget');
    this.selectedYear = this.getFiscalYear(new Date());
  },
  data: () => ({
    selectedYear: 2021,
    selectedProgram: null,
  }),
  methods: {
    getEncumberedFunds(fundingSource) {
      return fundingSource.projectFundings
        .filter((pf) => ['APPROVED', 'COMPLETED'].includes(pf.status.toUpperCase()))
        .map((pf) => pf.encumberedCostShareAmount)
        .reduce((totalAmount, lineItemAmount) => totalAmount + lineItemAmount, 0.0);
    },
    getRequestedFunds(fundingSource) {
      return fundingSource.projectFundings
        .filter((pf) => pf.status.toUpperCase() === 'SUBMITTED FOR PROJECT APPROVAL')
        .map((pf) => pf.requestedCostShareAmount)
        .reduce((totalAmount, lineItemAmount) => totalAmount + lineItemAmount, 0.0);
    },
    fundingSourcesForTable(selectedProgram) {
      return this.serverData
        .find((data) => data.year === this.selectedYear).fundingSources
        .filter((data) => data.programName === selectedProgram)
        .map((fs) => {
          const encumberedFunds = this.getEncumberedFunds(fs);
          const requestedFunds = this.getRequestedFunds(fs);
          return {
            id: fs.id,
            name: fs.name,
            totalBudget: fs.newProjectBudgetTotal,
            encumberedFunds,
            requestedFunds,
            remainingFunds: fs.newProjectBudgetTotal - encumberedFunds,
            projectFundings: fs.projectFundings,
          };
        });
    },
  },
};
</script>
